"use client"

import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/shared/components/ui/pagination"
import { buildUrl } from "@/shared/lib/utils"
import { usePathname, useSearchParams } from "next/navigation"
import { usePaginationData } from "../context/pagination-data-context"

function WorkOrdersTablePagination() {
    const pathname = usePathname()
    const searchParams = useSearchParams()

    const { paginationData } = usePaginationData()

    const { page, limit, total } = paginationData

    const totalPages = Math.ceil(total / limit)

    // Generate page links with proper URLs
    const getPageUrl = (pageNumber: number) => {
        return buildUrl(pathname, {
            ...Object.fromEntries(searchParams.entries()),
            page: pageNumber,
        })
    }

    // Create array of page numbers to display
    const getPageNumbers = () => {
        const pages = []
        const maxPagesToShow = 5

        if (totalPages <= maxPagesToShow) {
            // Show all pages if total pages are less than max to show
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            // Always show first page
            pages.push(1)

            // Calculate start and end of pages to show around current page
            let startPage = Math.max(2, page - 1)
            let endPage = Math.min(totalPages - 1, page + 1)

            // Adjust to show max visible pages
            if (endPage - startPage < maxPagesToShow - 3) {
                if (startPage === 2) {
                    endPage = Math.min(totalPages - 1, maxPagesToShow - 2)
                } else if (endPage === totalPages - 1) {
                    startPage = Math.max(2, totalPages - maxPagesToShow + 2)
                }
            }

            // Add ellipsis before middle pages if needed
            if (startPage > 2) {
                pages.push("ellipsis-start")
            }

            // Add middle pages
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i)
            }

            // Add ellipsis after middle pages if needed
            if (endPage < totalPages - 1) {
                pages.push("ellipsis-end")
            }

            // Always show last page
            if (totalPages > 1) {
                pages.push(totalPages)
            }
        }

        return pages
    }

    // Hide pagination if total is not available
    if (!total) return null

    return (
        <Pagination>
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious
                        href={page > 1 ? getPageUrl(page - 1) : "#"}
                        size={"sm"}
                        aria-disabled={page <= 1}
                        className={
                            page <= 1
                                ? "pointer-events-none text-muted-foreground"
                                : ""
                        }
                    />
                </PaginationItem>

                {getPageNumbers().map((pageNum, index) => {
                    if (
                        pageNum === "ellipsis-start" ||
                        pageNum === "ellipsis-end"
                    ) {
                        return (
                            <PaginationItem key={`ellipsis-${index}`}>
                                <PaginationEllipsis />
                            </PaginationItem>
                        )
                    }

                    return (
                        <PaginationItem key={pageNum}>
                            <PaginationLink
                                href={getPageUrl(pageNum as number)}
                                isActive={page === pageNum}
                            >
                                {pageNum}
                            </PaginationLink>
                        </PaginationItem>
                    )
                })}

                <PaginationItem>
                    <PaginationNext
                        href={page < totalPages ? getPageUrl(page + 1) : "#"}
                        size={"sm"}
                        aria-disabled={page >= totalPages}
                        className={
                            page >= totalPages
                                ? "pointer-events-none opacity-50"
                                : ""
                        }
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    )
}

export default WorkOrdersTablePagination
